import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import {
  Plus, Save, Trash2, Copy, Eye, EyeOff, X, Image, Wand2,
  ChevronUp, ChevronDown, Monitor, Tablet, Smartphone, GripVertical, Clock,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { ImageSourceSelector, SLIDER_SIZE_GUIDES } from "@/components/ImageSourceSelector";

const STATUS_COLORS = {
  published: "bg-green-500/10 text-green-400 border-green-500/30",
  draft: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  scheduled: "bg-blue-500/10 text-blue-400 border-blue-500/30",
};

function SortableSliderCard({ slider, onEdit, onDuplicate, onDelete, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: slider.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} className={cn(
      "bg-slate-800/50 border rounded-xl overflow-hidden transition-all",
      isDragging ? "border-blue-500 shadow-lg" : "border-slate-700/50",
      !slider.is_active && "opacity-50"
    )} data-testid={`slider-${slider.id}`}>
      <div className="flex items-stretch">
        <button {...attributes} {...listeners} className="px-3 bg-slate-800/80 cursor-grab active:cursor-grabbing text-slate-600 hover:text-slate-400 touch-none">
          <GripVertical size={16} />
        </button>
        <div className="w-32 h-20 bg-slate-900 flex-shrink-0 relative overflow-hidden">
          {slider.desktop_image ? (
            <img src={slider.desktop_image} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-600"><Image size={24} /></div>
          )}
        </div>
        <div className="flex-grow p-3 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-semibold text-white truncate">{slider.title_en || slider.title_fr || "Untitled Slide"}</h4>
            <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-semibold border", STATUS_COLORS[slider.status] || STATUS_COLORS.draft)}>{slider.status}</span>
            {slider.is_featured && <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 text-[10px] font-semibold">Featured</span>}
          </div>
          <p className="text-[11px] text-slate-500 truncate">{slider.subtitle_en || slider.subtitle_fr || "No subtitle"}</p>
          {(slider.start_date || slider.end_date) && (
            <div className="flex items-center gap-1 mt-1 text-[10px] text-slate-600">
              <Clock size={10} />
              {slider.start_date && <span>From: {new Date(slider.start_date).toLocaleDateString()}</span>}
              {slider.end_date && <span>Until: {new Date(slider.end_date).toLocaleDateString()}</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 px-3 flex-shrink-0">
          <button onClick={() => onToggle(slider)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400">{slider.is_active ? <Eye size={14} /> : <EyeOff size={14} />}</button>
          <button onClick={() => onEdit(slider)} className="p-1.5 rounded-lg hover:bg-slate-700 text-blue-400 text-xs font-medium">Edit</button>
          <button onClick={() => onDuplicate(slider)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400"><Copy size={14} /></button>
          <button onClick={() => onDelete(slider)} className="p-1.5 rounded-lg hover:bg-red-900/30 text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
}

const emptySlider = {
  title_fr: "", title_en: "", subtitle_fr: "", subtitle_en: "",
  description_fr: "", description_en: "",
  desktop_image: "", tablet_image: "", mobile_image: "",
  background_video: "", poster_image: "", overlay_opacity: 0.5,
  text_position: "left",
  btn1_label_fr: "", btn1_label_en: "", btn1_link: "", btn1_style: "primary",
  btn2_label_fr: "", btn2_label_en: "", btn2_link: "", btn2_style: "secondary",
  is_active: false, is_featured: false, start_date: "", end_date: "", status: "draft",
};

export default function AdminSliders() {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [langTab, setLangTab] = useState("en");
  const [generating, setGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const fetchSliders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/sliders`, { withCredentials: true });
      setSliders(res.data ?? []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load sliders");
    }
    setLoading(false);
  }, []);
  useEffect(() => { fetchSliders(); }, [fetchSliders]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const old = sliders.findIndex(s => s.id === active.id);
      const nw = sliders.findIndex(s => s.id === over.id);
      const reordered = arrayMove(sliders, old, nw);
      setSliders(reordered);
      await axios.put(`${API}/admin/sliders/reorder`, { order: reordered.map(s => s.id) }, { withCredentials: true });
    }
  };

  const createSlider = () => setEditModal({ ...emptySlider, _isNew: true });

  const saveSlider = async () => {
    if (!editModal) return;
    try {
      if (editModal._isNew) {
        const { _isNew, ...data } = editModal;
        await axios.post(`${API}/admin/sliders`, data, { withCredentials: true });
      } else {
        const { id, _isNew, created_at, created_by, version, display_order, ...data } = editModal;
        await axios.put(`${API}/admin/sliders/${editModal.id}`, data, { withCredentials: true });
      }
      setEditModal(null); fetchSliders();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (slider) => {
    await axios.put(`${API}/admin/sliders/${slider.id}`, { is_active: !slider.is_active }, { withCredentials: true });
    fetchSliders();
  };

  const duplicateSlider = async (slider) => {
    await axios.post(`${API}/admin/sliders/${slider.id}/duplicate`, {}, { withCredentials: true });
    fetchSliders();
  };

  const deleteSlider = async (slider) => {
    if (!confirm(`Delete "${slider.title_en || slider.title_fr}"?`)) return;
    await axios.delete(`${API}/admin/sliders/${slider.id}`, { withCredentials: true });
    fetchSliders();
  };

  const generateImage = async (field) => {
    if (!aiPrompt) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/generate-image`, { prompt: aiPrompt }, { withCredentials: true });
      setEditModal({ ...editModal, [field]: res.data.image_url });
      setAiPrompt("");
    } catch (e) { alert(e.response?.data?.detail || "Image generation failed"); }
    setGenerating(false);
  };

  const set = (key, val) => setEditModal(prev => ({ ...prev, [key]: val }));

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse" data-testid="sliders-loading">
        <div className="flex items-center justify-between"><div className="h-5 w-48 bg-slate-800 rounded" /><div className="h-9 w-32 bg-slate-800 rounded-lg" /></div>
        {[1, 2, 3].map(i => <div key={i} className="h-20 bg-slate-800/50 rounded-xl border border-slate-700/30" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4" data-testid="sliders-error">
        <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
          <Image size={24} className="text-red-400" />
        </div>
        <h3 className="text-lg font-semibold text-white">Failed to load sliders</h3>
        <p className="text-sm text-slate-400">{error}</p>
        <button onClick={fetchSliders} className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-500" data-testid="sliders-retry-btn">
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-slate-500">{sliders.length} slides · Drag to reorder</p>
        <button onClick={createSlider} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 flex items-center gap-2" data-testid="add-slider-btn">
          <Plus size={14} /> New Slide
        </button>
      </div>

      <div className="space-y-2" data-testid="sliders-list">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sliders.map(s => s.id)} strategy={verticalListSortingStrategy}>
            {sliders.map(s => (
              <SortableSliderCard key={s.id} slider={s} onEdit={() => setEditModal({ ...s })}
                onDuplicate={() => duplicateSlider(s)} onDelete={() => deleteSlider(s)} onToggle={() => toggleActive(s)} />
            ))}
          </SortableContext>
        </DndContext>
        {sliders.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-4" data-testid="sliders-empty">
            <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700/50 flex items-center justify-center">
              <Image size={28} className="text-slate-500" />
            </div>
            <div className="text-center">
              <h3 className="text-base font-semibold text-white mb-1">No hero slides found</h3>
              <p className="text-sm text-slate-500 max-w-sm">Create a slide for the DJILU homepage hero area.</p>
            </div>
            <button onClick={createSlider} className="px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 flex items-center gap-2" data-testid="add-first-slide-btn">
              <Plus size={14} /> Add First Slide
            </button>
          </div>
        )}
      </div>

      {/* ===== EDIT/CREATE MODAL ===== */}
      {editModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-4xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h3 className="text-base font-semibold text-white">{editModal._isNew ? "New Slide" : "Edit Slide"}</h3>
              <button onClick={() => setEditModal(null)} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-6 max-h-[75vh] overflow-y-auto">
              {/* Language Tabs */}
              <div className="flex gap-2">
                {["en", "fr"].map(l => (
                  <button key={l} onClick={() => setLangTab(l)} className={cn("px-4 py-2 rounded-lg text-sm font-medium",
                    langTab === l ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400")}>{l === "en" ? "English" : "Français"}</button>
                ))}
              </div>

              {/* Title/Subtitle/Description */}
              <div className="grid grid-cols-1 gap-4">
                <Field label={`Title (${langTab.toUpperCase()})`} value={editModal[`title_${langTab}`]} onChange={v => set(`title_${langTab}`, v)} testId="slider-title" />
                <Field label={`Subtitle (${langTab.toUpperCase()})`} value={editModal[`subtitle_${langTab}`]} onChange={v => set(`subtitle_${langTab}`, v)} />
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Description ({langTab.toUpperCase()})</label>
                  <textarea rows={3} value={editModal[`description_${langTab}`] || ""} onChange={e => set(`description_${langTab}`, e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
                </div>
              </div>

              {/* Responsive Hero Images */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3">Hero Images</h4>
                <div className="space-y-4">
                  <ImageSourceSelector label="Desktop Hero Image" deviceLabel="Desktop"
                    value={editModal.desktop_image} onChange={v => set("desktop_image", v)}
                    sizeGuide={SLIDER_SIZE_GUIDES.desktop} maxFileSize={4} />
                  <ImageSourceSelector label="Tablet Hero Image" deviceLabel="Tablet"
                    value={editModal.tablet_image} onChange={v => set("tablet_image", v)}
                    sizeGuide={SLIDER_SIZE_GUIDES.tablet} maxFileSize={3} />
                  <ImageSourceSelector label="Mobile Hero Image" deviceLabel="Mobile"
                    value={editModal.mobile_image} onChange={v => set("mobile_image", v)}
                    sizeGuide={SLIDER_SIZE_GUIDES.mobile} maxFileSize={3} />
                </div>
              </div>

              {/* Video & Overlay */}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Background Video URL" value={editModal.background_video} onChange={v => set("background_video", v)} />
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Overlay Opacity</label>
                  <input type="range" min="0" max="1" step="0.05" value={editModal.overlay_opacity} onChange={e => set("overlay_opacity", parseFloat(e.target.value))}
                    className="w-full" />
                  <span className="text-xs text-slate-500">{(editModal.overlay_opacity * 100).toFixed(0)}%</span>
                </div>
              </div>

              {/* Text Position */}
              <div>
                <label className="text-xs text-slate-500 mb-1.5 block">Text Position</label>
                <div className="flex gap-2">
                  {["left", "center", "right"].map(p => (
                    <button key={p} onClick={() => set("text_position", p)}
                      className={cn("px-4 py-2 rounded-lg text-sm capitalize", editModal.text_position === p ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400")}>{p}</button>
                  ))}
                </div>
              </div>

              {/* CTA Buttons */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3">CTA Buttons</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 bg-slate-800/30 rounded-lg p-3">
                    <p className="text-xs font-semibold text-slate-400">Button 1</p>
                    <Field label={`Label (${langTab.toUpperCase()})`} value={editModal[`btn1_label_${langTab}`]} onChange={v => set(`btn1_label_${langTab}`, v)} />
                    <Field label="Link" value={editModal.btn1_link} onChange={v => set("btn1_link", v)} />
                  </div>
                  <div className="space-y-2 bg-slate-800/30 rounded-lg p-3">
                    <p className="text-xs font-semibold text-slate-400">Button 2</p>
                    <Field label={`Label (${langTab.toUpperCase()})`} value={editModal[`btn2_label_${langTab}`]} onChange={v => set(`btn2_label_${langTab}`, v)} />
                    <Field label="Link" value={editModal.btn2_link} onChange={v => set("btn2_link", v)} />
                  </div>
                </div>
              </div>

              {/* Status & Scheduling */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Status</label>
                  <select value={editModal.status} onChange={e => set("status", e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" data-testid="slider-status-select">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="scheduled">Scheduled</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Start Date</label>
                  <input type="datetime-local" value={editModal.start_date?.slice(0, 16) || ""} onChange={e => set("start_date", e.target.value ? new Date(e.target.value).toISOString() : null)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">End Date</label>
                  <input type="datetime-local" value={editModal.end_date?.slice(0, 16) || ""} onChange={e => set("end_date", e.target.value ? new Date(e.target.value).toISOString() : null)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
                </div>
              </div>

              {/* Toggles */}
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editModal.is_active} onChange={e => set("is_active", e.target.checked)}
                    className="rounded border-slate-600 bg-slate-800 text-blue-500" />
                  <span className="text-sm text-slate-300">Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editModal.is_featured} onChange={e => set("is_featured", e.target.checked)}
                    className="rounded border-slate-600 bg-slate-800 text-amber-500" />
                  <span className="text-sm text-slate-300">Featured</span>
                </label>
              </div>

              {/* Preview */}
              {editModal.desktop_image && (
                <div>
                  <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">Preview</h4>
                  <div className="flex gap-2 mb-3">
                    {[{ d: "desktop", icon: Monitor, w: "100%" }, { d: "tablet", icon: Tablet, w: "70%" }, { d: "mobile", icon: Smartphone, w: "40%" }].map(p => (
                      <button key={p.d} onClick={() => setPreviewDevice(p.d)}
                        className={cn("p-1.5 rounded-lg", previewDevice === p.d ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400")}>
                        <p.icon size={16} />
                      </button>
                    ))}
                  </div>
                  <div className="bg-slate-950 rounded-xl overflow-hidden border border-slate-800 mx-auto transition-all"
                    style={{ maxWidth: previewDevice === "desktop" ? "100%" : previewDevice === "tablet" ? "70%" : "40%" }}>
                    <div className="relative aspect-[16/7]">
                      <img src={editModal[`${previewDevice}_image`] || editModal.desktop_image} alt="Preview" className="w-full h-full object-cover" />
                      <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${editModal.overlay_opacity})` }} />
                      <div className={cn("absolute inset-0 flex flex-col justify-end p-6",
                        editModal.text_position === "center" ? "items-center text-center" : editModal.text_position === "right" ? "items-end text-right" : "items-start")}>
                        <h2 className="text-xl font-bold text-white mb-1">{editModal[`title_${langTab}`] || "Title"}</h2>
                        <p className="text-sm text-white/70">{editModal[`subtitle_${langTab}`] || "Subtitle"}</p>
                        <div className="flex gap-2 mt-3">
                          {editModal[`btn1_label_${langTab}`] && <span className="px-4 py-2 bg-[#FFC928] text-black text-xs font-bold rounded-lg">{editModal[`btn1_label_${langTab}`]}</span>}
                          {editModal[`btn2_label_${langTab}`] && <span className="px-4 py-2 bg-white/10 text-white text-xs font-bold rounded-lg border border-white/20">{editModal[`btn2_label_${langTab}`]}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-800 flex justify-end gap-3">
              <button onClick={() => setEditModal(null)} className="px-4 py-2 rounded-lg bg-slate-800 text-slate-400 text-sm">Cancel</button>
              <button onClick={saveSlider} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-500" data-testid="save-slider-btn">
                <Save size={14} className="inline mr-1" /> {editModal._isNew ? "Create Slide" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-xs text-slate-500 mb-1.5 block">{label}</label>
      <input type="text" value={value || ""} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-blue-500 focus:outline-none"
        data-testid={testId} />
    </div>
  );
}

function ImageField({ label, value, onChange }) {
  return (
    <div>
      <label className="text-xs text-slate-500 mb-1.5 block">{label}</label>
      <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
        {value ? (
          <div className="relative">
            <img src={value} alt="" className="w-full h-24 object-cover" />
            <button onClick={() => onChange("")} className="absolute top-1 right-1 p-1 rounded bg-black/60 text-white"><X size={12} /></button>
          </div>
        ) : (
          <div className="h-24 flex items-center justify-center text-slate-600"><Image size={20} /></div>
        )}
        <input type="text" value={value || ""} onChange={e => onChange(e.target.value)} placeholder="Image URL..."
          className="w-full px-2 py-1.5 bg-transparent border-t border-slate-700 text-[11px] text-slate-400 focus:outline-none" />
      </div>
    </div>
  );
}
